import Vue from "vue";

/**
 * LME stickers carry `BRAND|LOT-CODE` as plain text, because that is what
 * outside scanners read. Internally we want the same result as any other
 * sticker, so this converts that text back into our own inventory record.
 *
 * It cannot be done by lot code alone across all history — `3N22-173-1` exists
 * 10 times and `K1-1` 147 times, so a blind lookup could open the wrong lot.
 * Restricted to inventory that is actually AVAILABLE the collisions almost
 * vanish: 5,752 distinct codes out of 5,795, and the worst tie is two lots.
 * So we search available stock only and hand any genuine tie back to the
 * caller to resolve, rather than picking one.
 */

/** `BRAND|CA26-157-1` -> { brand, lotCode, documentNumber } */
export function parseLmeQrValue(text) {
  if (typeof text !== "string") {
    return null;
  }
  const bar = text.indexOf("|");
  if (bar < 0) {
    return null;
  }
  const brand = text.slice(0, bar).trim();
  const lotCode = text.slice(bar + 1).trim();
  if (!lotCode) {
    return null;
  }
  // The lot code is `<documentNumber>-<lotIndex>`, and the document number can
  // itself contain hyphens (Mix6319-1-2-1), so split at the LAST one.
  const cut = lotCode.lastIndexOf("-");
  if (cut <= 0) {
    return null;
  }
  return { brand, lotCode, documentNumber: lotCode.slice(0, cut) };
}

/**
 * Find the in-stock lot an LME sticker refers to.
 *
 * Reuses the existing document search — no new endpoint — then matches the
 * exact unit code client-side.
 *
 * @returns {Promise<Array<{inventory: Object, unitDetail: Object}>>}
 *          empty when nothing is in stock under that code; more than one entry
 *          only in the rare tie the caller must resolve.
 */
export async function findAvailableLotsByCode({ documentNumber, lotCode }) {
  const resp = await Vue.axios.get(
    "/inventory/search/document/" + encodeURIComponent(documentNumber),
    { params: { page: 0, size: 50, sort: "id,desc", availableOnly: true } }
  );
  const rows = (resp && resp.data && resp.data.content) || [];

  const matches = [];
  rows.forEach((inventory) => {
    const units = inventory.unitDetails || [];
    units.forEach((unitDetail) => {
      if (unitDetail && unitDetail.code === lotCode) {
        matches.push({ inventory, unitDetail });
      }
    });
  });
  return matches;
}
